// Component for multiple selection with full React Hook Form integration and badge support

"use client";

import { Badge } from "@/components/ui/badge";
import { badgeColors } from "@/lib";
import { cn } from "@/lib/utils";
import { useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { FaXmark } from "react-icons/fa6";
import { SelectInput } from "./select-input";
import { SelectSearchInput } from "./select-search-input";
import { MobileCollapsibleBadges } from "./MobileCollapsibleBadges";

export type SelectItem = {
  id: string;
  name: string;
};

export type MultiSelectFieldProps = {
  name: string; // field that gets saved with react-hook-form
  options: SelectItem[];
  label?: string;
  placeholder?: string;
  onSearch?: (query: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loading?: boolean;
  className?: string;
  showFilter?: boolean;
  required?: boolean;
  useSearch?: boolean; // true for SelectSearchInput, false for SelectInput
  onBadgeClick?: (itemId: string) => void; // optional callback for badge clicks
  onBadgeRemove?: (itemId: string) => void; // optional callback when badge is removed
  showAllOption?: boolean; // show "All" option
  disabled?: boolean;
  noBorder?: boolean; // if true, remove border from inputs
  noShadow?: boolean; // if true, remove shadow from inputs
  bgStyle?: string; // additional background style for inputs (e.g. "bg-gray-100")
};

export function MultiSelectField({
  name,
  options,
  label,
  placeholder = "Select...",
  onSearch,
  hasMore = false,
  onLoadMore,
  loading = false,
  className = "",
  showFilter = true,
  required = false,
  useSearch = false,
  onBadgeClick,
  onBadgeRemove,
  showAllOption = false,
  disabled = false,
  noBorder = false,
  noShadow = false,
  bgStyle = "",
}: MultiSelectFieldProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  // Controller for the main array of values
  const {
    field: { value: rawSelectedItems, onChange: setSelectedItems },
  } = useController({
    name,
    control,
    defaultValue: [],
  }) as {
    field: {
      value: SelectItem[] | any;
      onChange: (value: SelectItem[]) => void;
    };
  };

  // Ensure selectedItems is always an array
  const selectedItems = useMemo(() => {
    if (Array.isArray(rawSelectedItems)) {
      return rawSelectedItems;
    }
    if (!rawSelectedItems) {
      return [];
    }
    // If it's a string or other type, return empty array
    return [];
  }, [rawSelectedItems, name]);

  // Controller for the temporary select
  const {
    field: { value: tempItem, onChange: setTempItem },
  } = useController({
    name: `__temp_${name}`,
    control,
    defaultValue: null,
  }) as {
    field: {
      value: SelectItem | null;
      onChange: (value: SelectItem | null) => void;
    };
  };

  // Effect to handle when an element is selected
  useEffect(() => {
    if (tempItem) {
      // If "All" is selected, add a pseudo category to enable dependent fields
      if (tempItem.id === "all") {
        const existsAll = selectedItems.some((item) => item.id === "all");
        if (!existsAll) {
          setSelectedItems([...selectedItems, { id: "all", name: "All" }]);
        }
        setTempItem(null);
        if (onBadgeClick) onBadgeClick("all");
        return;
      }

      // Check if this item already exists
      const exists = selectedItems.some((item) => item.id === tempItem.id);

      if (!exists) {
        setSelectedItems([...selectedItems, tempItem]);
      }

      // Always trigger filter when selecting from dropdown (even if exists)
      if (onBadgeClick) {
        onBadgeClick(tempItem.id);
      }

      // Reset temporary select
      setTempItem(null);
    }
  }, [tempItem, selectedItems, setSelectedItems, setTempItem, onBadgeClick]);

  const removeBadge = (itemId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const updatedItems = selectedItems.filter((item) => item.id !== itemId);

    setSelectedItems(updatedItems);

    // Notify parent when badge is removed
    if (onBadgeRemove) {
      onBadgeRemove(itemId);
    }
  };

  const handleBadgeClick = (itemId: string) => {
    if (onBadgeClick) {
      onBadgeClick(itemId);
    }
  };

  // Add "All" option if needed
  const selectedIds = useMemo(() => {
    return new Set((selectedItems || []).map((item) => String(item.id)));
  }, [selectedItems]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => !selectedIds.has(String(option.id)));
  }, [options, selectedIds]);

  const allOptions = useMemo(() => {
    if (!showAllOption) return filteredOptions;
    const includeAll = !selectedIds.has("all");
    return includeAll
      ? [{ id: "all", name: "All" }, ...filteredOptions]
      : filteredOptions;
  }, [filteredOptions, showAllOption, selectedIds]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <label className="block text-sm font-semibold text-gray-600 whitespace-nowrap mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="w-full">
        {useSearch ? (
          <SelectSearchInput
            name={`__temp_${name}`}
            items={allOptions}
            placeholder={placeholder}
            onSearch={onSearch}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            loading={loading}
            onResetPage={() => {}}
            className={`w-full ${bgStyle}`}
            showFilter={showFilter}
            disabled={disabled}
            noBorder={noBorder}
            noShadow={noShadow}
          />
        ) : (
          <SelectInput
            name={`__temp_${name}`}
            options={allOptions}
            placeholder={placeholder}
            className={`w-full ${bgStyle}`}
            disabled={disabled}
            noBorder={noBorder}
            noShadow={noShadow}
          />
        )}
      </div>

      {/* Badges section */}
      {/* Mobile Collapsible Badges */}
      <MobileCollapsibleBadges
        items={(Array.isArray(selectedItems) ? selectedItems : []).map(
          (item) => ({ id: item.id, name: item.name }),
        )}
        sectionTitle={label || "Selected Items"}
        onItemClick={onBadgeClick}
        onItemRemove={
          onBadgeRemove
            ? (itemId) => {
                const updatedItems = selectedItems.filter(
                  (item) => item.id !== itemId,
                );
                setSelectedItems(updatedItems);
                onBadgeRemove(itemId);
              }
            : undefined
        }
        disabled={disabled}
        className="mt-2"
      />

      {/* Desktop Badges (unchanged) */}
      <div className="mt-2 hidden md:flex md:flex-wrap md:gap-2">
        {(Array.isArray(selectedItems) ? selectedItems : []).map(
          (item, idx) => (
            <Badge
              key={`desktop-${idx}-${item.id || ""}`}
              className={cn(
                "flex items-center gap-1 pr-1 px-3 py-1 text-sm",
                badgeColors[idx % badgeColors.length],
              )}
            >
              <span
                className={cn(
                  "flex-1 select-none",
                  onBadgeClick && !disabled
                    ? "cursor-pointer"
                    : "cursor-default",
                )}
                onClick={disabled ? undefined : () => handleBadgeClick(item.id)}
              >
                {item.name || "(no name)"}
              </span>
              {!disabled && (
                <button
                  type="button"
                  className="h-4 w-4 ml-1 flex items-center justify-center hover:opacity-80"
                  onClick={(event) => removeBadge(item.id, event)}
                >
                  <FaXmark className="h-4 w-4" />
                </button>
              )}
            </Badge>
          ),
        )}
      </div>

      {/* Error message */}
      {errors[name]?.message && (
        <span className="text-red-500 text-xs mt-1 block">
          {String(errors[name]?.message)}
        </span>
      )}
    </div>
  );
}
